import type { TagTone } from '../../ui';

/**
 * DUE DATES, read the way a person reads them (notes-tasks-comments.md 12.2).
 *
 * A due date is a SORT KEY AND A BADGE, not a scheduler - there are no reminders in v1,
 * which is exactly why the badge has to carry the urgency the absent reminder would
 * have. "2026-08-02" tells a reader nothing at a glance; "Overdue" and "Today" tell them
 * everything.
 *
 * The whole file works in CALENDAR DAYS, never in instants. A due date has no time and
 * no zone (D5), so comparing it against `Date.now()` would make a task due today read as
 * overdue for anybody west of UTC after lunch.
 */

export type DueBucket = 'overdue' | 'today' | 'week' | 'later' | 'none';

/** Today as a plain `YYYY-MM-DD` in the reader's own calendar, which is the one they mean. */
export function todayKey(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * String comparison, deliberately: `YYYY-MM-DD` sorts lexicographically exactly as it
 * sorts chronologically, so this needs no parsing and cannot drift by a timezone.
 */
export function bucketOf(dueDate: string | null, now: Date = new Date()): DueBucket {
  if (dueDate === null) return 'none';
  const today = todayKey(now);
  if (dueDate < today) return 'overdue';
  if (dueDate === today) return 'today';
  const horizon = new Date(now);
  horizon.setDate(horizon.getDate() + 7);
  return dueDate <= todayKey(horizon) ? 'week' : 'later';
}

export const DUE_BUCKET_LABEL: Readonly<Record<DueBucket, string>> = {
  overdue: 'Overdue',
  today: 'Today',
  week: 'This week',
  later: 'Later',
  none: 'No due date',
};

/** The order the groups read in: what is late first, what is undated last. */
export const DUE_BUCKET_ORDER: readonly DueBucket[] = ['overdue', 'today', 'week', 'later', 'none'];

export interface DueBadge {
  readonly label: string;
  readonly tone: TagTone;
}

/**
 * The badge on a task row, or null when there is nothing worth saying.
 *
 * A DONE task never shows an overdue badge. It was finished; when it was due stopped
 * mattering the moment somebody ticked it, and a red badge on completed work is a
 * reproach for a job already done.
 */
export function dueBadge(dueDate: string | null, done: boolean, locale?: string): DueBadge | null {
  if (dueDate === null) return null;
  if (done) return { label: formatDue(dueDate, locale), tone: 'neutral' };
  const bucket = bucketOf(dueDate);
  switch (bucket) {
    case 'overdue':
      return { label: `Overdue: ${formatDue(dueDate, locale)}`, tone: 'danger' };
    case 'today':
      return { label: 'Due today', tone: 'warning' };
    default:
      return { label: formatDue(dueDate, locale), tone: 'neutral' };
  }
}

/** `2026-08-02` as `2 Aug`, and with the year only when it is not this one. */
export function formatDue(dueDate: string, locale?: string): string {
  const [year, month, day] = dueDate.split('-');
  if (year === undefined || month === undefined || day === undefined) return dueDate;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(date.getTime())) return dueDate;
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}
